import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router';
import { ManageService } from '../../../../../services/manage.service';
import { CommonService } from '../../../../../services';
import { combineLatest, Observable, Subject } from 'rxjs';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, Validators } from '@angular/forms';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Budget, Notifications, Request } from '../../../../../models/budget.model';

@Component({
  selector: 'app-budget-and-alerts-edit',
  templateUrl: './budget-and-alerts-edit.component.html',
  styleUrls: ['./budget-and-alerts-edit.component.css']
})
export class BudgetAndAlertsEditComponent implements OnInit {

  public tenantId: string;
  public editMode = true;
  public loading = false;
  public scopeId: string;
  public budget: any;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public markDisabled = (date: NgbDate) => { return this.calendar.getWeekday(date) >= 0 };

  public budgetDetailDataSrc = [
    {
      type: 'Critical',
      controlName: 'criticalAlert'
    },
    {
      type: 'Warning',
      controlName: 'warningAlert'
    }
  ];
  public displayedColumns: string[] = [
    'type',
    'percent',
    'amount'
  ];

  public budgetAndAlertsForm = this.fb.group({
    name: [ { value: null, disabled: false }, [Validators.required, Validators.pattern('^[a-zA-Z0-9-_]+$')]],
    timeGrain: [ { value: null, disabled: false }, Validators.required],
    amount: [ { value: null, disabled: false }, Validators.required],
    startDate:  [
      { 
        value: {
          year: this.calendar.getToday().year,
          month: this.calendar.getToday().month,
          day: 1
        },
        disabled: false 
      },
      Validators.required
    ],
    endDate: [{ value: {}, disabled: false }],
    criticalAlert: [{ value: null, disabled: false }, Validators.required],
    warningAlert: [{ value: null, disabled: false }, Validators.required],
    newEmail: [{ value: null, disabled: false }],
  });

  public emails: string[] = []


  constructor(
    private manageService: ManageService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public commonService: CommonService,
    public calendar: NgbCalendar,
    private fb: FormBuilder
  ) {}

  async ngAfterViewInit(): Promise<void> {
    await this.commonService.waitForElm('#levelOne');
    $('#levelOne').animate({ scrollLeft: $('#managecsp').width() + 505 + $('#azureSubscription').width()}, 400);
  }

  ngOnInit() {
    combineLatest([
      this.activatedRoute.parent.parent.params,
      this.manageService.currentBudgetDetail,
      this.activatedRoute.params
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([tenantParams, currentBudget, subParams]) => {
        this.tenantId = tenantParams['tenantId'];
        this.budget = currentBudget;
        
        if (!this.commonService.isEmpty(this.budget)) {
          this.processBudgetAlertData();
        } else if (this.commonService.isEmpty(this.budget) && !subParams['budgetName']) {
          this.editMode = false;
          this.scopeId = subParams['scopeId'] == this.tenantId ? null : subParams['scopeId'];
        } else {
          this.manageService
            .GetBudgetAlertByName(this.tenantId, subParams['budgetName'])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((result: any) => {
              this.budget = result
              this.processBudgetAlertData();
            },  (error: any) => {
              this.commonService.implementToast(error, 'Error');
            })
        }

      });

      this.budgetAndAlertsForm.get('newEmail').valueChanges.pipe(
        debounceTime(2000)
      ).pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
        const emailCheck = /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;
        emailCheck
        if (emailCheck.test(value)) {
          this.emails.push(value);
          this.budgetAndAlertsForm.get('newEmail').setValue(null);
        } else if (value) {
          this.commonService.implementToast('Please enter a valid email address', 'Error');
        }
      });
  }

  private processBudgetAlertData(): void {
    this.budgetAndAlertsForm.get('name').setValue(this.budget?.name);
    this.budgetAndAlertsForm.get('name').disable();

    this.budgetAndAlertsForm.get('timeGrain').setValue(this.budget?.properties?.timeGrain);
    this.budgetAndAlertsForm.get('timeGrain').disable();

    const startDate = new Date(this.budget?.properties?.timePeriod?.startDate);
    const endDate = new Date(this.budget?.properties?.timePeriod?.endDate);
    this.budgetAndAlertsForm.get('startDate').setValue({
      year: startDate.getFullYear(),
      month: startDate.getMonth() + 1,
      day: startDate.getDate()
    });
    this.budgetAndAlertsForm.get('endDate').setValue({
      year: endDate.getFullYear(),
      month: endDate.getMonth() + 1,
      day: endDate.getDate()
    });
    this.budgetAndAlertsForm.get('endDate').disable();
    this.budgetAndAlertsForm.get('startDate').disable();

    this.budgetAndAlertsForm.get('amount').setValue(this.budget?.properties?.amount);
    this.budgetAndAlertsForm.get('criticalAlert').setValue(this.budget?.properties?.notifications?.criticalAlert?.threshold);
    this.budgetAndAlertsForm.get('warningAlert').setValue(this.budget?.properties?.notifications?.warningAlert?.threshold);
    
    this.emails = [ ...this.budget?.properties?.notifications?.criticalAlert?.contactEmails || []  ];
  }

  public editBudget(): void {
    this.router.navigate([`BudgetAlertEdit/${this.budget.name}`], { relativeTo: this.activatedRoute });
  }

  // calling API
  public saveOrCreateButton(): void {
    this.loading = true;
    this.budgetAndAlertsForm.disable();
    const action$ = this.editMode ? this.updateBudget() : this.createBudget();

    action$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: any) => {
        this.manageService.refetchBudgetList$.next();
        this.commonService.implementToast(`Budget ${this.editMode ? 'updated' : 'created'} successfully!`, 'Success');
        if (result?.name) {
          this.manageService.monitorCurrentBudget(null);
          this.router.navigate([`../../BudgetAlertDetails/${result.name}`], { relativeTo: this.activatedRoute })
        } else {
          this.close();
        }
      },  (error: any) => {
        this.loading = false;
        this.budgetAndAlertsForm.enable();
        this.commonService.implementToast(`Budget ${this.editMode ? 'updated' : 'created'} failed!`, 'Error');
      })
  }

  // Update Budget
  private updateBudget(): Observable<any> {
    const notifications: Notifications = {};

    notifications.criticalAlert = {
      contactEmails: [ ...this.emails ],
      threshold: this.budgetAndAlertsForm.get('criticalAlert').value
    }

    notifications.warningAlert = {
      contactEmails: [ ...this.emails ],
      threshold: this.budgetAndAlertsForm.get('warningAlert').value
    }

    return this.manageService.updateBudget(
      this.tenantId,
      this.budgetAndAlertsForm.get('name').value,
      this.budgetAndAlertsForm.get('amount').value,
      this.budget?.properties?.filter?.dimensions?.values?.[0],
      notifications
    )
  }

  // Create Budget
  private createBudget(): Observable<any>  {
    const request: Request = {
      properties: {
        amount: this.budgetAndAlertsForm.get('amount').value,
        timeGrain: this.budgetAndAlertsForm.get('timeGrain').value,
        category: 'Cost',
        notifications: {
          criticalAlert: {
            contactEmails: [ ...this.emails ],
            threshold: this.budgetAndAlertsForm.get('criticalAlert').value,
            enabled: true,
            operator: 'GreaterThan',
            locale: 'en-us',
            thresholdType: 'Actual'
          },
          warningAlert: {
            contactEmails: [ ...this.emails ],
            threshold: this.budgetAndAlertsForm.get('warningAlert').value,
            enabled: true,
            operator: 'GreaterThan',
            locale: 'en-us',
            thresholdType: 'Actual'
          }
        },
        timePeriod: {
          startDate: new Date(Date.UTC(
            this.budgetAndAlertsForm.get('startDate').value.year,
            this.budgetAndAlertsForm.get('startDate').value.month - 1,
            this.budgetAndAlertsForm.get('startDate').value.day
          )),
          endDate: new Date(Date.UTC(
            this.budgetAndAlertsForm.get('endDate').value.year,
            this.budgetAndAlertsForm.get('endDate').value.month - 1,
            this.budgetAndAlertsForm.get('endDate').value.day
          ))
        }
      }
    }
    return this.manageService.createBudget(
      this.tenantId,
      this.budgetAndAlertsForm.get('name').value,
      this.scopeId ? this.scopeId : '',
      request
    )
  }

  public onCreateDateSelection(date: NgbDate, datepicker: any, next: any): void {
    if (
      !this.budgetAndAlertsForm.get('startDate') ||
      (next.month != this.budgetAndAlertsForm.get('startDate').value.month) ||
      (next.year != this.budgetAndAlertsForm.get('startDate').value.year)
    ) {
      datepicker.close();
      this.budgetAndAlertsForm.get('startDate').setValue({
        year: next.year,
        month: next.month,
        day: 1
      });
    }
  }

  public deleteEmail(emailIndex): void {
    this.emails.splice(emailIndex, 1)
  }

  public openDateSelect(ele: any, controlName: string): void {
    if (this.budgetAndAlertsForm.get(controlName).enabled) {
      ele.toggle()
    }
  }

  close() {
    this.manageService.monitorCurrentBudget(null);
    this.budgetAndAlertsForm.reset();
    $('#levelOne').animate({ scrollLeft: $('#managecsp').width() + 505 }, 400);
    this.router.navigate([`../../`], { relativeTo: this.activatedRoute });
  }

}
